import React, { useEffect, useState, useContext, useMemo } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, X, Workflow } from 'lucide-react';
import { usePageHeader } from '../components/Layout';
import { Avatar, SectionCard, Chip, EmptyState } from '../design/primitives';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const EMPTY_STEP = {
  stepName: '',
  assignToType: 'user',
  assignToId: '',
  stepType: 'Üretim',
  tagId: '',
  quantityType: 'adet',
  quantityValue: '0',
};

export default function Workflows() {
  const { user } = useContext(AuthContext);
  const { activeWorkspaceId, isWorkspaceAdmin } = useWorkspace();
  // Backend require_workspace_admin gates workflow create/update/delete in workspace context.
  const canManage = activeWorkspaceId
    ? isWorkspaceAdmin
    : !!user && (
        user.role === 'admin' ||
        user.role === 'super_admin' ||
        user.permissions?.includes('all') ||
        user.permissions?.includes('can_manage_workflows')
      );

  const [workflows, setWorkflows] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const emptyForm = { name: '', description: '', steps: [] };
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  const [draftStep, setDraftStep] = useState(EMPTY_STEP);
  const [editDraftStep, setEditDraftStep] = useState(EMPTY_STEP);
  const [editingStepIdx, setEditingStepIdx] = useState(null);

  useEffect(() => {
    Promise.all([fetchWorkflows(), fetchUsers(), fetchTags()]).catch(() => {});
  }, []);

  const fetchWorkflows = async () => {
    try {
      const res = await axios.get(`${API}/workflows`);
      const sorted = [...res.data].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'tr')
      );
      setWorkflows(sorted);
    } catch {
      /* noop */
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch {
      /* noop */
    }
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API}/tags`);
      setTags(res.data);
    } catch {
      /* noop */
    }
  };

  const userById = (id) => users.find((u) => u.id === id);
  const tagById = (id) => tags.find((t) => t.id === id);

  const totalSteps = useMemo(
    () => workflows.reduce((sum, w) => sum + (w.steps?.length || 0), 0),
    [workflows]
  );

  const addStepToCreate = () => {
    if (!draftStep.stepName || !draftStep.assignToId || !draftStep.tagId) {
      toast.error('Adım adı, kategori ve sorumlu kullanıcı zorunludur');
      return;
    }
    setCreateForm((f) => ({
      ...f,
      steps: [...f.steps, { ...draftStep, order: f.steps.length }],
    }));
    setDraftStep(EMPTY_STEP);
  };

  const removeCreateStep = (i) => {
    setCreateForm((f) => ({
      ...f,
      steps: f.steps
        .filter((_, idx) => idx !== i)
        .map((s, idx) => ({ ...s, order: idx })),
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (createForm.steps.length === 0) {
      toast.error('En az bir adım eklemelisiniz');
      return;
    }
    try {
      await axios.post(`${API}/workflows`, createForm);
      toast.success('İş akışı oluşturuldu');
      setCreateOpen(false);
      setCreateForm(emptyForm);
      setDraftStep(EMPTY_STEP);
      fetchWorkflows();
    } catch (err) {
      toast.error(
        'Oluşturulamadı: ' + (err.response?.data?.detail || err.message)
      );
    }
  };

  const openEdit = (w) => {
    setSelected(w);
    setEditForm({
      name: w.name,
      description: w.description || '',
      steps: w.steps || [],
    });
    setEditingStepIdx(null);
    setEditDraftStep(EMPTY_STEP);
    setEditOpen(true);
  };

  const addStepToEdit = () => {
    if (!editDraftStep.stepName || !editDraftStep.assignToId || !editDraftStep.tagId) {
      toast.error('Adım adı, kategori ve sorumlu kullanıcı zorunludur');
      return;
    }
    setEditForm((f) => ({
      ...f,
      steps: [...f.steps, { ...editDraftStep, order: f.steps.length }],
    }));
    setEditDraftStep(EMPTY_STEP);
  };

  const startEditingEditStep = (i) => {
    setEditDraftStep({ ...EMPTY_STEP, ...editForm.steps[i] });
    setEditingStepIdx(i);
  };

  const saveEditingEditStep = () => {
    if (!editDraftStep.stepName || !editDraftStep.assignToId || !editDraftStep.tagId) {
      toast.error('Adım adı, kategori ve sorumlu kullanıcı zorunludur');
      return;
    }
    setEditForm((f) => ({
      ...f,
      steps: f.steps.map((s, idx) =>
        idx === editingStepIdx ? { ...editDraftStep, order: idx } : s
      ),
    }));
    setEditingStepIdx(null);
    setEditDraftStep(EMPTY_STEP);
  };

  const cancelEditingEditStep = () => {
    setEditingStepIdx(null);
    setEditDraftStep(EMPTY_STEP);
  };

  const removeEditStep = (i) => {
    setEditForm((f) => ({
      ...f,
      steps: f.steps
        .filter((_, idx) => idx !== i)
        .map((s, idx) => ({ ...s, order: idx })),
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (editForm.steps.length === 0) {
      toast.error('En az bir adım eklemelisiniz');
      return;
    }
    try {
      await axios.patch(`${API}/workflows/${selected.id}`, editForm);
      toast.success('İş akışı güncellendi');
      setEditOpen(false);
      fetchWorkflows();
    } catch (err) {
      toast.error(
        'Güncellenemedi: ' + (err.response?.data?.detail || err.message)
      );
    }
  };

  const openDelete = (w) => {
    setSelected(w);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/workflows/${selected.id}`);
      toast.success('İş akışı silindi');
      setDeleteOpen(false);
      fetchWorkflows();
    } catch (err) {
      toast.error('Silinemedi: ' + (err.response?.data?.detail || err.message));
    }
  };

  const breadcrumb = useMemo(() => [{ label: 'İş Akışları' }], []);
  const headerActions = useMemo(() => {
    if (!canManage) return null;
    return (
      <button
        className="ox-btn ox-btn-primary"
        onClick={() => {
          setCreateForm(emptyForm);
          setDraftStep(EMPTY_STEP);
          setCreateOpen(true);
        }}
      >
        <Plus size={14} /> Yeni İş Akışı
      </button>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);
  usePageHeader(breadcrumb, headerActions, [canManage]);

  return (
    <div className="ox-page space-y-5" data-testid="workflows-page">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatTile label="Toplam İş Akışı" value={workflows.length} />
        <StatTile label="Toplam Adım" value={totalSteps} />
        <StatTile
          label="Ort. Adım / Akış"
          value={
            workflows.length
              ? (totalSteps / workflows.length).toFixed(1)
              : '0'
          }
        />
      </div>

      {/* Grid */}
      {workflows.length === 0 ? (
        <div className="ox-surface">
          <EmptyState
            icon={<Workflow size={32} />}
            title="Henüz iş akışı yok"
            description="Görev otomasyonu için ilk iş akışını oluşturun. Adımlar belirleyip görevlerin standart bir akışta ilerlemesini sağlayın."
            action={canManage && (
              <button
                className="ox-btn ox-btn-primary"
                onClick={() => {
                  setCreateForm(emptyForm);
                  setDraftStep(EMPTY_STEP);
                  setCreateOpen(true);
                }}
              >
                <Plus size={14} /> İlk İş Akışını Oluştur
              </button>
            )}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {workflows.map((wf) => (
            <SectionCard
              key={wf.id}
              pad={false}
              title={
                <div className="flex items-center gap-2">
                  <span>{wf.name}</span>
                  <Chip>{wf.steps?.length || 0} adım</Chip>
                </div>
              }
              subtitle={wf.description || '—'}
              right={
                canManage ? (
                  <div className="flex items-center gap-1">
                    <button
                      className="ox-btn ox-btn-ghost"
                      onClick={() => openEdit(wf)}
                      title="Düzenle"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      className="ox-btn ox-btn-ghost"
                      style={{ color: 'var(--danger)' }}
                      onClick={() => openDelete(wf)}
                      title="Sil"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : null
              }
            >
              <div className="p-4">
                {wf.steps?.length ? (
                  <ol className="space-y-1.5">
                    {wf.steps.slice(0, 6).map((step, i) => {
                      const u = userById(step.assignToId);
                      const t = tagById(step.tagId);
                      const isControl = step.stepType === 'Kontrol';
                      return (
                        <li
                          key={i}
                          className="ox-row-hover flex items-center gap-2 px-2 py-1.5 rounded-md"
                        >
                          <span className="ox-step-num">{i + 1}</span>
                          <span className="flex-1 min-w-0 text-sm truncate">
                            {step.stepName}
                          </span>
                          <Chip
                            color={isControl ? 'var(--review)' : 'var(--accent)'}
                            bg={isControl ? 'var(--review-weak)' : 'var(--accent-weak)'}
                          >
                            {step.stepType || 'Üretim'}
                          </Chip>
                          {t && <Chip>{t.name}</Chip>}
                          {u && (
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Avatar u={u} imgSrc={u.picture} size={18} />
                              <span className="text-xs ox-text-muted truncate max-w-[90px]">
                                {u.name}
                              </span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                    {wf.steps.length > 6 && (
                      <li className="text-xs text-[color:var(--text-muted)] px-2">
                        +{wf.steps.length - 6} adım daha
                      </li>
                    )}
                  </ol>
                ) : (
                  <div className="text-xs text-[color:var(--text-muted)] italic">
                    Henüz adım eklenmemiş
                  </div>
                )}
              </div>
            </SectionCard>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {createOpen && (
        <div className="ox-modal-backdrop" onClick={() => setCreateOpen(false)}>
          <div className="ox-modal ox-modal-lg" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleCreate}>
              <div className="ox-modal-head">
                <h3>Yeni İş Akışı</h3>
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setCreateOpen(false)}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="ox-modal-body space-y-4">
                <div className="grid grid-cols-1 gap-3">
                  <div className="ox-field">
                    <label>İş Akışı Adı *</label>
                    <input
                      value={createForm.name}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, name: e.target.value })
                      }
                      className="ox-input"
                      placeholder="Örn: Sosyal Medya İçerik Üretimi"
                      required
                    />
                  </div>
                  <div className="ox-field">
                    <label>Açıklama</label>
                    <textarea
                      value={createForm.description}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          description: e.target.value,
                        })
                      }
                      className="ox-textarea"
                      rows={2}
                      placeholder="Kısa açıklama"
                    />
                  </div>
                </div>

                <StepEditor
                  steps={createForm.steps}
                  onRemove={removeCreateStep}
                  draft={draftStep}
                  setDraft={setDraftStep}
                  onAdd={addStepToCreate}
                  users={users}
                  tags={tags}
                />
              </div>
              <div className="ox-modal-foot">
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setCreateOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="ox-btn ox-btn-primary">
                  Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editOpen && selected && (
        <div className="ox-modal-backdrop" onClick={() => setEditOpen(false)}>
          <div className="ox-modal ox-modal-lg" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleUpdate}>
              <div className="ox-modal-head">
                <h3>İş Akışını Düzenle</h3>
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setEditOpen(false)}
                >
                  <X size={14} />
                </button>
              </div>
              <div className="ox-modal-body space-y-4">
                <div className="grid grid-cols-1 gap-3">
                  <div className="ox-field">
                    <label>İş Akışı Adı *</label>
                    <input
                      value={editForm.name}
                      onChange={(e) =>
                        setEditForm({ ...editForm, name: e.target.value })
                      }
                      className="ox-input"
                      required
                    />
                  </div>
                  <div className="ox-field">
                    <label>Açıklama</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          description: e.target.value,
                        })
                      }
                      className="ox-textarea"
                      rows={2}
                    />
                  </div>
                </div>

                <StepEditor
                  steps={editForm.steps}
                  onRemove={removeEditStep}
                  onEdit={startEditingEditStep}
                  editingIdx={editingStepIdx}
                  draft={editDraftStep}
                  setDraft={setEditDraftStep}
                  onAdd={addStepToEdit}
                  onSaveEdit={saveEditingEditStep}
                  onCancelEdit={cancelEditingEditStep}
                  users={users}
                  tags={tags}
                />
              </div>
              <div className="ox-modal-foot">
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  onClick={() => setEditOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="ox-btn ox-btn-primary">
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteOpen && selected && (
        <div className="ox-modal-backdrop" onClick={() => setDeleteOpen(false)}>
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <h3>İş Akışını Sil</h3>
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setDeleteOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body">
              <p className="text-sm">
                <strong>{selected.name}</strong> iş akışını silmek
                istediğinizden emin misiniz?
              </p>
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setDeleteOpen(false)}
              >
                İptal
              </button>
              <button className="ox-btn ox-btn-danger" onClick={handleDelete}>
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StepEditor({
  steps,
  onRemove,
  onEdit,
  editingIdx,
  draft,
  setDraft,
  onAdd,
  onSaveEdit,
  onCancelEdit,
  users,
  tags,
}) {
  const userById = (id) => users.find((u) => u.id === id);
  const tagById = (id) => tags.find((t) => t.id === id);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold">Adımlar</h4>
        <span className="text-xs text-[color:var(--text-muted)]">
          {steps.length} adım
        </span>
      </div>

      {steps.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {steps.map((s, i) => {
            const u = userById(s.assignToId);
            const t = tagById(s.tagId);
            const isControl = s.stepType === 'Kontrol';
            return (
              <div
                key={i}
                className="flex items-center gap-2 px-2 py-1.5 ox-surface-muted rounded-md"
              >
                <span className="ox-step-num">{i + 1}</span>
                <span className="flex-1 min-w-0 text-sm truncate">
                  {s.stepName}
                </span>
                <Chip
                  color={isControl ? 'var(--review)' : 'var(--accent)'}
                  bg={isControl ? 'var(--review-weak)' : 'var(--accent-weak)'}
                >
                  {s.stepType || 'Üretim'}
                </Chip>
                {t && <Chip>{t.name}</Chip>}
                {u && (
                  <div className="flex items-center gap-1.5">
                    <Avatar u={u} imgSrc={u.picture} size={18} />
                    <span className="text-xs ox-text-muted truncate max-w-[80px]">
                      {u.name}
                    </span>
                  </div>
                )}
                {s.quantityType && s.quantityValue && (
                  <span className="text-xs ox-text-muted ox-mono">
                    {s.quantityValue}
                    {s.quantityType === 'adet' ? ' adet' : ''}
                  </span>
                )}
                {onEdit && (
                  <button
                    type="button"
                    className="ox-btn ox-btn-ghost"
                    onClick={() => onEdit(i)}
                    title="Düzenle"
                  >
                    <Edit2 size={12} />
                  </button>
                )}
                <button
                  type="button"
                  className="ox-btn ox-btn-ghost"
                  style={{ color: 'var(--danger)' }}
                  onClick={() => onRemove(i)}
                  title="Sil"
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="ox-surface-muted p-3 rounded-md space-y-2" style={{ borderStyle: 'dashed' }}>
        <div className="flex items-center justify-between">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
            {editingIdx != null ? `${editingIdx + 1}. adımı düzenle` : 'Yeni adım'}
          </h5>
          {editingIdx != null && (
            <button
              type="button"
              className="ox-btn ox-btn-ghost text-xs"
              onClick={onCancelEdit}
            >
              İptal
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="ox-field">
            <label>Adım Adı *</label>
            <input
              value={draft.stepName}
              onChange={(e) =>
                setDraft({ ...draft, stepName: e.target.value })
              }
              className="ox-input"
              placeholder="Örn: İçerik Yazımı"
            />
          </div>
          <div className="ox-field">
            <label>Adım Tipi *</label>
            <select
              value={draft.stepType}
              onChange={(e) =>
                setDraft({ ...draft, stepType: e.target.value })
              }
              className="ox-select"
            >
              <option value="Üretim">Üretim</option>
              <option value="Kontrol">Kontrol</option>
            </select>
          </div>
          <div className="ox-field">
            <label>Kategori *</label>
            <select
              value={draft.tagId}
              onChange={(e) => setDraft({ ...draft, tagId: e.target.value })}
              className="ox-select"
            >
              <option value="">— Seçin —</option>
              {tags.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="ox-field">
            <label>Sorumlu Kullanıcı *</label>
            <select
              value={draft.assignToId}
              onChange={(e) =>
                setDraft({ ...draft, assignToId: e.target.value })
              }
              className="ox-select"
            >
              <option value="">— Seçin —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div className="ox-field">
            <label>Miktar Tipi</label>
            <select
              value={draft.quantityType}
              onChange={(e) => {
                const v = e.target.value;
                setDraft({
                  ...draft,
                  quantityType: v,
                  quantityValue: v === 'adet' ? '0' : '00:00:00',
                });
              }}
              className="ox-select"
            >
              <option value="adet">Adet</option>
              <option value="süre">Süre (HH:MM:SS)</option>
            </select>
          </div>
          <div className="ox-field">
            <label>Varsayılan Değer</label>
            <input
              value={draft.quantityValue}
              onChange={(e) =>
                setDraft({ ...draft, quantityValue: e.target.value })
              }
              className="ox-input"
              placeholder={draft.quantityType === 'adet' ? '0' : '00:00:00'}
            />
          </div>
        </div>
        {editingIdx != null ? (
          <button
            type="button"
            className="ox-btn ox-btn-primary w-full justify-center"
            onClick={onSaveEdit}
          >
            Değişiklikleri Kaydet
          </button>
        ) : (
          <button
            type="button"
            className="ox-btn ox-btn-ghost w-full justify-center"
            onClick={onAdd}
            style={{ borderStyle: 'dashed', borderColor: 'var(--border-strong)', color: 'var(--text-muted)' }}
          >
            <Plus size={14} /> Adım Ekle
          </button>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="ox-surface p-4">
      <div className="text-[11px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide">
        {label}
      </div>
      <div className="text-2xl font-semibold ox-mono mt-1">{value}</div>
    </div>
  );
}
